import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, X, Send, Loader2, History, Plus, Trash2 } from "lucide-react";
import { formatDateShort } from "@/utils/format";

const SUGGESTED_QUESTIONS = [
  "¿Cómo va el negocio este mes?",
  "¿Puedo permitirme una nueva contratación?",
  "¿Qué cliente me da más margen?",
  "¿Qué debería priorizar esta semana?",
];

export const MAX_QUESTION_LENGTH = 2000;
const COUNTER_THRESHOLD = 1600;

// Formato inline: **negrita** y *cursiva*. Devuelve un array de nodos React
// (los strings no necesitan key; los elementos la llevan).
// La cursiva exige que el `*` de apertura no vaya seguido de espacio y el de
// cierre no vaya precedido de espacio: así un asterisco aislado («2 * 3») o el
// de una viñeta se dejan literales en vez de comerse el texto.
function renderInline(text, keyPrefix) {
  const nodes = [];
  const regex = /\*\*([^*]+)\*\*|\*(?!\s)([^*]+?)(?<!\s)\*/g;
  let last = 0;
  let k = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[1] !== undefined) {
      nodes.push(<strong key={`${keyPrefix}-b${k++}`}>{m[1]}</strong>);
    } else {
      nodes.push(<em key={`${keyPrefix}-i${k++}`}>{m[2]}</em>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

// Mini-formatter de markdown ligero para las respuestas del asistente:
// **negrita**, *cursiva*, viñetas «- », «* » o «• » como lista, «1. » como
// lista numerada y saltos de línea. Sin dependencias nuevas y sin
// dangerouslySetInnerHTML. (Gemini escribe las viñetas con `*   ` — smoke
// 30-08-2026, B3: antes solo se entendía «- » y el `*` acababa como cursiva.)
const BULLET_LINE = /^\s*[-*•]\s+(.*)$/;
const NUMBERED_LINE = /^\s*\d+[.)]\s+(.*)$/;

export function renderLightMarkdown(text) {
  const lines = String(text ?? "").split("\n");
  const blocks = [];
  let list = null; // { type: "ul" | "ol", items: [] }

  const flushList = (key) => {
    if (!list) return;
    const Tag = list.type;
    const className =
      list.type === "ol" ? "list-decimal pl-5 space-y-0.5" : "list-disc pl-4 space-y-0.5";
    blocks.push(
      <Tag key={key} className={className}>
        {list.items}
      </Tag>
    );
    list = null;
  };

  const pushItem = (type, content, i) => {
    if (list && list.type !== type) flushList(`${list.type}-${i}`);
    if (!list) list = { type, items: [] };
    list.items.push(<li key={`li-${i}`}>{renderInline(content, `li-${i}`)}</li>);
  };

  lines.forEach((line, i) => {
    const bullet = BULLET_LINE.exec(line);
    const numbered = bullet ? null : NUMBERED_LINE.exec(line);
    if (bullet) {
      pushItem("ul", bullet[1], i);
    } else if (numbered) {
      pushItem("ol", numbered[1], i);
    } else if (line.trim() === "") {
      flushList(`list-${i}`);
      blocks.push(<div key={`sp-${i}`} className="h-1.5" aria-hidden="true" />);
    } else {
      flushList(`list-${i}`);
      blocks.push(<p key={`p-${i}`}>{renderInline(line, `p-${i}`)}</p>);
    }
  });
  flushList("list-end");
  return blocks;
}

// Contenido del chat CFO compartido entre el panel fijo de escritorio y la
// hoja inferior móvil: header + cuerpo scrollable + input. El estado vive en
// AiChat (siempre montado) para que la conversación sobreviva a cerrar/abrir.
export default function AiChatPanel({
  view,
  messages,
  loading,
  input,
  conversations,
  historyLoading,
  historyError,
  activeConversationId,
  messagesEndRef,
  onInputChange,
  onSend,
  onSendSuggested,
  onKeyDown,
  onToggleHistory,
  onNewConversation,
  onSelectConversation,
  onDeleteConversation,
  onClose,
  mobile = false,
}) {
  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-900 text-white shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="w-4 h-4 shrink-0" />
          <span className="text-sm font-semibold truncate">
            {view === "history" ? "Conversaciones" : "CFO Asistente IA"}
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            data-testid="aichat-new"
            onClick={onNewConversation}
            className="hover:bg-slate-700 rounded p-1"
            title="Nueva conversación"
          >
            <Plus className="w-4 h-4" />
          </button>
          <button
            data-testid="aichat-history"
            onClick={onToggleHistory}
            className={`hover:bg-slate-700 rounded p-1 ${view === "history" ? "bg-slate-700" : ""}`}
            title={view === "history" ? "Volver al chat" : "Historial de conversaciones"}
          >
            <History className="w-4 h-4" />
          </button>
          <button
            data-testid="aichat-close"
            onClick={onClose}
            className="hover:bg-slate-700 rounded p-1"
            title="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Cuerpo scrollable */}
      <div className="grow overflow-y-auto p-4 space-y-3">
        {view === "history" ? (
          <div className="space-y-2">
            {historyLoading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Cargando conversaciones…
              </div>
            ) : historyError ? (
              <p className="py-6 text-center text-sm text-slate-500">{historyError}</p>
            ) : (conversations ?? []).length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-500">
                Aún no hay conversaciones guardadas.
              </p>
            ) : (
              conversations.map((c) => {
                const count = c.message_count ?? 0;
                const isActive = c.conversation_id === activeConversationId;
                return (
                  <div
                    key={c.conversation_id}
                    className={`flex items-center rounded-lg border transition-colors ${
                      isActive
                        ? "border-slate-400 bg-slate-100"
                        : "border-slate-200 bg-slate-50 hover:bg-slate-100"
                    }`}
                  >
                    <button
                      data-testid={`aichat-conv-${c.conversation_id}`}
                      onClick={() => onSelectConversation(c.conversation_id)}
                      className="min-w-0 flex-1 px-3 py-2 text-left"
                    >
                      <p className="truncate text-xs font-medium text-slate-800">
                        {c.title || "Conversación"}
                      </p>
                      <p className="mt-0.5 text-[10.5px] text-slate-500">
                        {formatDateShort(c.updated_at)} · {count}{" "}
                        {count === 1 ? "mensaje" : "mensajes"}
                      </p>
                    </button>
                    <button
                      data-testid={`aichat-del-${c.conversation_id}`}
                      onClick={() => onDeleteConversation(c.conversation_id)}
                      className="shrink-0 p-2 mr-1 text-slate-400 hover:text-rose-600"
                      title="Borrar conversación"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        ) : (
          <>
            {messages.length === 0 && (
              <div className="space-y-3">
                <p className="text-sm text-slate-500 text-center mt-4">
                  Pregúntame sobre tus finanzas
                </p>
                <div className="space-y-2">
                  {SUGGESTED_QUESTIONS.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => onSendSuggested(q)}
                      className="block w-full text-left text-xs px-3 py-2 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-700 transition-colors border border-slate-200"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                    msg.role === "user"
                      ? "bg-slate-900 text-white"
                      : "bg-slate-100 text-slate-800"
                  }`}
                >
                  {msg.role === "assistant" ? (
                    <div className="leading-relaxed space-y-1">
                      {renderLightMarkdown(msg.content)}
                    </div>
                  ) : (
                    <div className="whitespace-pre-wrap leading-relaxed">{msg.content}</div>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-slate-100 rounded-lg px-3 py-2 flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Escribiendo...
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input (solo en la vista de chat) */}
      {view === "chat" && (
        <div
          className={`shrink-0 border-t border-slate-200 p-3 ${
            mobile ? "pb-[max(0.75rem,env(safe-area-inset-bottom))]" : ""
          }`}
        >
          {input.length > COUNTER_THRESHOLD && (
            <p className="mb-1 text-right text-[10px] text-slate-400 tabular-nums">
              {input.length}/{MAX_QUESTION_LENGTH}
            </p>
          )}
          <div className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => onInputChange(e.target.value)}
              onKeyDown={onKeyDown}
              maxLength={MAX_QUESTION_LENGTH}
              placeholder="Pregunta algo..."
              className="flex-1 text-sm"
              disabled={loading}
            />
            <Button
              size="sm"
              onClick={onSend}
              disabled={!input.trim() || loading}
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
